import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { CustomAttribute } from '~/shared/data-contract/custom-attribute';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import { Input } from '~/ui/core/input/Input';
import { Select, SelectItem } from '~/ui/core/select/Select';

import { isValidUrlInput, urlMaxLength } from '../data-contract';
import type { LearningOpportunityFormState } from '../form-state';
import { FieldLabel } from './FieldLabel';

const fieldClassName = 'min-w-[150px] max-w-[220px] flex-1';

const CustomMetadataField: FunctionComponent<{
    readonly attribute: CustomAttribute;
}> = ({ attribute }) => {
    const translate = useTranslate();
    const { control } = useFormContext<LearningOpportunityFormState>();
    const name = attribute.key;
    const label = translate(`attributes.learningOpportunity.${attribute.name}` as TranslationKey);

    switch (attribute.type) {
        case 'boolean':
            return (
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <FieldLabel className={fieldClassName}>
                            {label}
                            <div className="flex h-[42px] items-center">
                                <Checkbox
                                    aria-label={label}
                                    checked={is.boolean(field.value) ? field.value : false}
                                    disabled={attribute.isReadonly}
                                    onBlur={field.onBlur}
                                    onCheckedChange={(checked) => field.onChange(is.boolean(checked) ? checked : false)}
                                />
                            </div>
                        </FieldLabel>
                    )}
                />
            );
        case 'date':
            return (
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <FieldLabel className={fieldClassName}>
                            {label}
                            <Input
                                aria-label={label}
                                disabled={attribute.isReadonly}
                                type="date"
                                value={is.string(field.value) ? field.value.slice(0, 10) : ''}
                                onBlur={field.onBlur}
                                onChange={(event) => field.onChange(event.target.value || null)}
                            />
                        </FieldLabel>
                    )}
                />
            );
        case 'integer':
            return (
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <FieldLabel className={fieldClassName}>
                            {label}
                            <Input
                                aria-label={label}
                                disabled={attribute.isReadonly}
                                type="number"
                                value={is.number(field.value) ? field.value : ''}
                                onBlur={field.onBlur}
                                onChange={(event) =>
                                    field.onChange(
                                        is.emptyString(event.target.value) ? null : Number(event.target.value),
                                    )
                                }
                            />
                        </FieldLabel>
                    )}
                />
            );
        case 'string':
            return (
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <FieldLabel className={fieldClassName}>
                            {label}
                            {is.undefined(attribute.allowedValues) || is.emptyArray(attribute.allowedValues) ? (
                                <Input
                                    aria-label={label}
                                    disabled={attribute.isReadonly}
                                    value={is.string(field.value) ? field.value : ''}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                />
                            ) : (
                                <Select
                                    disabled={attribute.isReadonly}
                                    value={is.string(field.value) ? field.value : undefined}
                                    onValueChange={field.onChange}
                                >
                                    {attribute.allowedValues.map((allowedValue) => (
                                        <SelectItem key={allowedValue} value={allowedValue}>
                                            {translate(
                                                `attributes.learningOpportunity.${attribute.name}-options.${allowedValue}` as TranslationKey,
                                            )}
                                        </SelectItem>
                                    ))}
                                </Select>
                            )}
                        </FieldLabel>
                    )}
                />
            );
    }
};

export const EditableMetadataFields: FunctionComponent<{
    readonly customAttributes: readonly CustomAttribute[];
    readonly learningLanguageCodes: readonly string[];
    readonly providerOptions: readonly string[];
}> = ({ customAttributes, learningLanguageCodes, providerOptions }) => {
    const translate = useTranslate();
    const { control } = useFormContext<LearningOpportunityFormState>();

    return (
        <div className="flex flex-wrap items-end gap-l">
            <Controller
                control={control}
                name="learningLanguage"
                render={({ field }) => (
                    <FieldLabel className={fieldClassName}>
                        {translate('pages.learning-opportunities.learning-language')}
                        <Select value={field.value} onValueChange={field.onChange}>
                            {learningLanguageCodes.map((code) => (
                                <SelectItem key={code} value={code}>
                                    {translate(`common.languages.${code}` as TranslationKey)}
                                </SelectItem>
                            ))}
                        </Select>
                    </FieldLabel>
                )}
            />
            <Controller
                control={control}
                name="provider"
                render={({ field }) => (
                    <FieldLabel className={fieldClassName}>
                        {translate('pages.learning-opportunities.provider')}
                        <Select value={field.value} onValueChange={field.onChange}>
                            {providerOptions.map((provider) => (
                                <SelectItem key={provider} value={provider}>
                                    {translate(`providers.${provider}` as TranslationKey)}
                                </SelectItem>
                            ))}
                        </Select>
                    </FieldLabel>
                )}
            />
            <Controller
                control={control}
                name="url"
                rules={{ validate: (value) => isValidUrlInput(value ?? '') }}
                render={({ field, fieldState }) => (
                    <FieldLabel className="min-w-[200px] flex-1">
                        {translate('pages.learning-opportunities.url')}
                        <Input
                            {...field}
                            aria-invalid={fieldState.invalid}
                            maxLength={urlMaxLength}
                            value={is.string(field.value) ? field.value : ''}
                            variant={fieldState.invalid ? 'error' : 'default'}
                        />
                        {fieldState.invalid && (
                            <span className="text-body-xs font-normal text-text-error">
                                {translate('pages.learning-opportunities.url-invalid')}
                            </span>
                        )}
                    </FieldLabel>
                )}
            />
            {customAttributes.map((attribute) => (
                <CustomMetadataField key={`${attribute.key}-${attribute.type}`} attribute={attribute} />
            ))}
        </div>
    );
};
